import type { IconName } from "@@/shared/icon-name";
import type { Translatable} from "@@/shared/translations/translation";
import { translation } from "@@/shared/translations/translation";
import { toObjectMap } from "@@/utilities/dictionary";
import { unique } from "@@/utilities/unique";
import { z } from "zod";

const hzAlignments = ["left", "center", "right", "auto"] as const;
const vtAlignments = ["top", "center", "bottom"] as const;
type HzAlignment = (typeof hzAlignments)[number];
type VtAlignment = (typeof vtAlignments)[number];
type AlignmentValue = VtAlignment | HzAlignment;
const alignments = unique([...hzAlignments, ...vtAlignments]);
const alignment = toObjectMap<AlignmentValue>([...alignments]);

const hzAlignment = toObjectMap<HzAlignment>([...hzAlignments]);
const vtAlignment = toObjectMap<VtAlignment>([...vtAlignments]);

const hzAlignmentZodSchema = z.enum(hzAlignments);
const vtAlignmentZodSchema = z.enum(vtAlignments);
const alignmentZodSchema = z.object({
    vertical: vtAlignmentZodSchema,
    horizontal: hzAlignmentZodSchema,
});

const hzAlignmentMeta: Record<
    HzAlignment,
    {
        name: Translatable;
        iconName: IconName;
    }
> = {
    left: {
        name: translation({
            sv: "Vänster",
            en: "Left",
        }),
        iconName: "fa-align-left",
    },
    center: {
        name: translation({
            sv: "Centrerad",
            en: "Center",
        }),
        iconName: "fa-align-center",
    },
    right: {
        name: translation({
            sv: "Höger",
            en: "Right",
        }),
        iconName: "fa-align-right",
    },
    auto: {
        name: translation({
            sv: "Auto",
            en: "Auto",
        }),
        iconName: "fa-wand-magic-sparkles",
    },
};

type Alignment = z.infer<typeof alignmentZodSchema>;

export {
    alignment,
    alignmentZodSchema,
    alignments,
    hzAlignment,
    hzAlignmentMeta,
    hzAlignmentZodSchema,
    hzAlignments,
    vtAlignment,
    vtAlignmentZodSchema,
    vtAlignments,
};
export type { Alignment, AlignmentValue, HzAlignment, VtAlignment };
